// Admin dashboard — what needs attention today and how the period is going across
// Leads, Attendance, Tasks and Employees. One API call (/api/dashboard/overview).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area, Legend,
} from "recharts";
import {
  Users, UserCheck, ClipboardList, PhoneCall, RefreshCw, ArrowRight, AlertTriangle, Clock, TrendingUp, TrendingDown,
  Loader2, CalendarDays, Target, IndianRupee, Briefcase, Minus,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const IST = { timeZone: "Asia/Kolkata" };
const today = () => new Date().toLocaleDateString("en-CA", IST);
const addDays = (ymd, n) => { const d = new Date(`${ymd}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const PRESETS = [
  ["today", "Today", () => [today(), today()]],
  ["week", "This week", () => { const t = today(); const dow = (new Date(`${t}T00:00:00Z`).getUTCDay() + 6) % 7; return [addDays(t, -dow), t]; }],
  ["month", "This month", () => { const t = today(); return [`${t.slice(0, 7)}-01`, t]; }],
  ["last_month", "Last month", () => { const t = today(); const first = `${t.slice(0, 7)}-01`; const end = addDays(first, -1); return [`${end.slice(0, 7)}-01`, end]; }],
  ["quarter", "Last 90 days", () => { const t = today(); return [addDays(t, -89), t]; }],
];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const time = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { ...IST, hour: "2-digit", minute: "2-digit", hour12: true }) : "—");
const day = (iso) => (iso ? new Date(String(iso).length <= 10 ? `${iso}T00:00:00` : iso).toLocaleDateString("en-IN", { ...IST, day: "2-digit", month: "short" }) : "—");
const initials = (n) => (n || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const greeting = () => { const h = Number(new Date().toLocaleString("en-US", { ...IST, hour: "numeric", hour12: false })); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };

const ATT = {
  working: ["Working", "bg-emerald-50 text-emerald-700 ring-emerald-200", "#10b981"],
  punched_out: ["Punched out", "bg-slate-100 text-slate-600 ring-slate-200", "#64748b"],
  absent: ["Absent", "bg-rose-50 text-rose-700 ring-rose-200", "#f43f5e"],
  not_in_yet: ["Not in yet", "bg-amber-50 text-amber-700 ring-amber-200", "#f59e0b"],
  on_leave: ["On leave", "bg-violet-50 text-violet-700 ring-violet-200", "#8b5cf6"],
  off: ["Day off", "bg-slate-50 text-slate-500 ring-slate-200", "#cbd5e1"],
};
const Chip = ({ status }) => { const m = ATT[status] || ATT.off; return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${m[1]}`}>{m[0]}</span>; };

function Delta({ now, prev, suffix = "vs previous period" }) {
  if (prev === undefined || prev === null) return null;
  if (!prev && !now) return <span className="text-[11px] text-slate-400">No activity either period</span>;
  if (!prev) return <span className="text-[11px] text-slate-500">New this period · {suffix}</span>;
  const d = Math.round(((now - prev) / prev) * 100);
  const Icon = d > 0 ? TrendingUp : d < 0 ? TrendingDown : Minus;
  return <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${d > 0 ? "text-emerald-600" : d < 0 ? "text-rose-600" : "text-slate-500"}`}><Icon className="h-3 w-3" />{d > 0 ? "+" : ""}{d}% {suffix}</span>;
}

function Section({ icon: Icon, tint, title, subtitle, link, linkLabel, children, testid }) {
  return (
    <section className="flex min-w-0 flex-col rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid={testid}>
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tint}`}><Icon className="h-[18px] w-[18px]" /></span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900">{title}</h2>
            {subtitle && <p className="truncate text-xs text-slate-500">{subtitle}</p>}
          </div>
        </div>
        {link && <Link to={link} className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800">{linkLabel}<ArrowRight className="h-3.5 w-3.5" /></Link>}
      </header>
      <div className="flex-1 p-5">{children}</div>
    </section>
  );
}
const Stat = ({ label, value, sub, tone = "text-slate-900" }) => (
  <div className="rounded-xl bg-slate-50 px-3 py-2.5">
    <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`mt-0.5 text-xl font-semibold tabular-nums ${tone}`}>{value}</div>
    {sub && <div className="mt-0.5">{sub}</div>}
  </div>
);
const Empty = ({ children }) => <div className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-sm text-slate-400">{children}</div>;

export default function AdminOverview({ currentUser, businessId, businessName }) {
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(() => PRESETS.find((p) => p[0] === "month")[2]());
  const [custom, setCustom] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attFilter, setAttFilter] = useState("all");
  const [empSort, setEmpSort] = useState("score");

  const load = useCallback((quiet) => {
    if (!quiet) setLoading(true);
    const q = new URLSearchParams({ from: range[0], to: range[1] });
    if (businessId) q.set("business_id", businessId);
    return fetch(`${BACKEND_URL}/api/dashboard/overview?${q}`, { credentials: "include" })
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.detail || "Could not load the dashboard"); return d; })
      .then(setData).catch((e) => !quiet && toast.error(e.message)).finally(() => setLoading(false));
  }, [range, businessId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => load(true), 120000); return () => clearInterval(t); }, [load]);

  const pick = (key) => { const p = PRESETS.find((x) => x[0] === key); setPreset(key); setCustom(false); setRange(p[2]()); };
  const periodLabel = custom ? `${day(range[0])} – ${day(range[1])}` : PRESETS.find((p) => p[0] === preset)?.[1];

  const employees = useMemo(() => {
    if (!data) return [];
    const list = data.employees.slice();
    const cmp = {
      score: () => 0,
      overdue: (a, b) => b.tasks_overdue - a.tasks_overdue,
      open: (a, b) => b.tasks_open - a.tasks_open,
      present: (a, b) => b.days_present - a.days_present,
      name: (a, b) => a.name.localeCompare(b.name),
    }[empSort];
    return empSort === "score" ? list : list.sort(cmp);
  }, [data, empSort]);

  if (!data) {
    return <div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  const { leads: L, attendance: A, tasks: T } = data;
  const firstName = (currentUser?.name || "").trim().split(/\s+/)[0] || "there";
  const people = A.people.filter((p) => attFilter === "all" || (attFilter === "late" ? p.is_late : attFilter === "present" ? ["working", "punched_out"].includes(p.status) : p.status === attFilter));
  const openStages = L.by_stage.filter((s) => s.kind === "open");
  const maxStage = Math.max(1, ...openStages.map((s) => s.count));
  const daysSinceLead = L.last_lead_at ? Math.floor((Date.now() - new Date(L.last_lead_at).getTime()) / 86400000) : null;

  // Things that need someone to act today, in priority order.
  const alerts = [
    A.absent > 0 && !A.is_off_day && { tone: "rose", text: `${A.absent} absent today`, to: "/attendance/records" },
    T.overdue > 0 && { tone: "rose", text: `${T.overdue} overdue task${T.overdue === 1 ? "" : "s"}`, to: "/work-hub/tasks?filter=overdue" },
    L.followups.overdue > 0 && { tone: "amber", text: `${L.followups.overdue} lead follow-up${L.followups.overdue === 1 ? "" : "s"} past due`, to: "/leads" },
    L.unassigned_open > 0 && { tone: "amber", text: `${L.unassigned_open} open lead${L.unassigned_open === 1 ? "" : "s"} unassigned`, to: "/leads" },
    T.unassigned_open > 0 && { tone: "amber", text: `${T.unassigned_open} task${T.unassigned_open === 1 ? "" : "s"} unassigned`, to: "/work-hub/tasks" },
    daysSinceLead !== null && daysSinceLead >= 14 && { tone: "slate", text: `No new leads in ${daysSinceLead} days`, to: "/leads" },
  ].filter(Boolean);

  return (
    <div className="space-y-5" data-testid="admin-overview">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">{new Date().toLocaleDateString("en-IN", { ...IST, weekday: "long", day: "numeric", month: "long", year: "numeric" })}{businessName ? ` · ${businessName}` : ""}</p>
          <h1 className="text-2xl font-semibold text-slate-900 sm:text-3xl">{greeting()}, {firstName}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-white p-1">
            {PRESETS.map(([k, label]) => (
              <button key={k} onClick={() => pick(k)} data-testid={`dash-period-${k}`}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${!custom && preset === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{label}</button>
            ))}
            <button onClick={() => setCustom((v) => !v)} className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${custom ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Custom</button>
          </div>
          {custom && (
            <div className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2 py-1">
              <input type="date" value={range[0]} max={range[1]} onChange={(e) => e.target.value && setRange([e.target.value, range[1]])} className="rounded border-0 text-xs text-slate-700" />
              <span className="text-slate-400">–</span>
              <input type="date" value={range[1]} min={range[0]} max={today()} onChange={(e) => e.target.value && setRange([range[0], e.target.value])} className="rounded border-0 text-xs text-slate-700" />
            </div>
          )}
          <button onClick={() => load()} title="Refresh" className="rounded-xl border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
      </div>

      {/* ── Today at a glance ── */}
      <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 p-5 text-white shadow-lg">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-300">Today</h2>
          <span className="text-xs text-slate-400">Updated {time(data.generated_at)} · refreshes every 2 min</span>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Link to="/attendance/records" className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10 transition hover:bg-white/10">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-300"><UserCheck className="h-4 w-4 text-emerald-300" />Attendance</div>
            <div className="mt-2 text-3xl font-semibold">{A.present}<span className="text-lg text-slate-400">/{A.total_staff}</span></div>
            <div className="mt-1 text-xs text-slate-300">{A.is_off_day ? (A.holiday || "Weekly off") : <>present{A.late ? <> · <span className="text-amber-300">{A.late} late</span></> : ""}{A.absent ? <> · <span className="text-rose-300">{A.absent} absent</span></> : ""}{A.not_in_yet ? ` · ${A.not_in_yet} not in yet` : ""}</>}</div>
          </Link>
          <Link to="/work-hub/tasks" className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10 transition hover:bg-white/10">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-300"><ClipboardList className="h-4 w-4 text-sky-300" />Tasks due today</div>
            <div className="mt-2 text-3xl font-semibold">{T.due_today}</div>
            <div className="mt-1 text-xs text-slate-300">{T.overdue ? <span className="text-rose-300">{T.overdue} overdue</span> : "Nothing overdue"} · {T.open} open</div>
          </Link>
          <Link to="/leads" className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10 transition hover:bg-white/10">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-300"><PhoneCall className="h-4 w-4 text-amber-300" />Lead follow-ups</div>
            <div className="mt-2 text-3xl font-semibold">{L.followups.today}<span className="text-lg text-slate-400"> today</span></div>
            <div className="mt-1 text-xs text-slate-300">{L.followups.overdue ? <span className="text-amber-300">{L.followups.overdue} past due</span> : "None past due"} · {L.followups.next_7_days} in next 7 days</div>
          </Link>
          <Link to="/leads" className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10 transition hover:bg-white/10">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-300"><Target className="h-4 w-4 text-violet-300" />New leads today</div>
            <div className="mt-2 text-3xl font-semibold">{L.new_today}</div>
            <div className="mt-1 text-xs text-slate-300">Last lead {L.last_lead_at ? `${day(L.last_lead_at)}${daysSinceLead ? ` (${daysSinceLead}d ago)` : ""}` : "—"}</div>
          </Link>
        </div>
        {alerts.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {alerts.map((a) => (
              <Link key={a.text} to={a.to} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset transition hover:brightness-110 ${a.tone === "rose" ? "bg-rose-500/15 text-rose-200 ring-rose-400/30" : a.tone === "amber" ? "bg-amber-500/15 text-amber-200 ring-amber-400/30" : "bg-white/10 text-slate-200 ring-white/20"}`}>
                <AlertTriangle className="h-3.5 w-3.5" />{a.text}
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        {/* ── Leads ── */}
        <Section testid="dash-leads" icon={Target} tint="bg-violet-50 text-violet-600" title="Leads" subtitle={`${periodLabel} · ${L.total} leads in total`} link="/leads" linkLabel="All leads">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="New" value={L.new_in_period} sub={<Delta now={L.new_in_period} prev={L.new_prev_period} suffix="vs prev." />} />
            <Stat label="Won" value={L.won_in_period} tone="text-emerald-600" sub={<span className="text-[11px] text-slate-500">{inr(L.won_value_in_period)}</span>} />
            <Stat label="Lost" value={L.lost_in_period} tone="text-rose-600" sub={<span className="text-[11px] text-slate-500">{L.conversion_rate}% win rate</span>} />
            <Stat label="Open pipeline" value={L.open} sub={<span className="text-[11px] text-slate-500">{inr(L.pipeline_value)}</span>} />
          </div>

          {L.new_in_period > 0 ? (
            <div className="mt-4 h-40">
              <ResponsiveContainer>
                <AreaChart data={L.trend} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                  <defs><linearGradient id="dl" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.35} /><stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="new" name="New leads" stroke="#8b5cf6" strokeWidth={2} fill="url(#dl)" />
                  <Area type="monotone" dataKey="won" name="Won" stroke="#10b981" strokeWidth={2} fill="none" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 ring-1 ring-amber-200">
              No new leads in this period{L.last_lead_at ? ` — the last one arrived on ${day(L.last_lead_at)}` : ""}. Check that your lead sources (website form, Facebook, Sheets) are still connected.
            </div>
          )}

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Open pipeline by stage</h3>
              {openStages.length === 0 ? <Empty>No open leads</Empty> : (
                <div className="space-y-1.5">
                  {openStages.slice(0, 6).map((s) => (
                    <div key={s.stage} className="flex items-center gap-2 text-sm">
                      <span className="w-28 truncate text-slate-600">{s.stage}</span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-violet-500" style={{ width: `${(s.count / maxStage) * 100}%` }} /></div>
                      <span className="w-8 text-right font-semibold tabular-nums text-slate-800">{s.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Lead owners · follow-ups due</h3>
              <div className="space-y-1.5">
                {L.owners.slice(0, 6).map((o) => (
                  <div key={o.user_id || "none"} className="flex items-center gap-2 text-sm">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-50 text-[10px] font-bold text-violet-700">{o.user_id ? initials(o.name) : "—"}</span>
                    <span className={`flex-1 truncate ${o.user_id ? "text-slate-700" : "text-slate-400"}`}>{o.name}</span>
                    <span className="text-xs text-slate-500">{o.open} open</span>
                    {o.followups_due > 0 && <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">{o.followups_due} due</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <h3 className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Follow-ups due (most recent first)</h3>
          {L.followups.list.length === 0 ? <Empty>No follow-ups due</Empty> : (
            <ul className="divide-y divide-slate-100 rounded-xl border border-slate-100">
              {L.followups.list.slice(0, 5).map((l) => {
                const late = String(l.next_meeting).slice(0, 10) < data.today;
                return (
                  <li key={l.lead_id}>
                    <Link to="/leads" className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-slate-900">{l.name}</div>
                        <div className="truncate text-xs text-slate-500">{l.stage} · {l.owner}</div>
                      </div>
                      <span className={`shrink-0 text-xs font-medium ${late ? "text-rose-600" : "text-slate-600"}`}>{day(l.next_meeting)}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>

        {/* ── Attendance ── */}
        <Section testid="dash-attendance" icon={UserCheck} tint="bg-emerald-50 text-emerald-600" title="Attendance" subtitle={`Today · office opens ${A.office_start}`} link="/attendance" linkLabel="Attendance">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Present" value={`${A.present}/${A.total_staff}`} tone="text-emerald-600" sub={<span className="text-[11px] text-slate-500">{A.attendance_rate}% attendance</span>} />
            <Stat label="Late" value={A.late} tone={A.late ? "text-amber-600" : "text-slate-900"} />
            <Stat label={A.not_in_yet ? "Not in yet" : "Absent"} value={A.not_in_yet || A.absent} tone={(A.absent || A.not_in_yet) ? "text-rose-600" : "text-slate-900"} />
            <Stat label="On leave" value={A.on_leave} tone="text-violet-600" sub={A.wfh ? <span className="text-[11px] text-slate-500">{A.wfh} WFH</span> : null} />
          </div>

          <div className="mt-4 h-36">
            <ResponsiveContainer>
              <BarChart data={A.trend} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} domain={[0, A.total_staff]} />
                <Tooltip />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="present" name="Present" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="late" name="Late" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {[["all", "Everyone", A.total_staff], ["present", "Present", A.present], ["late", "Late", A.late], ["absent", "Absent", A.absent], ["not_in_yet", "Not in yet", A.not_in_yet], ["on_leave", "On leave", A.on_leave]]
              .filter(([k, , n]) => k === "all" || n > 0).map(([k, label, n]) => (
                <button key={k} onClick={() => setAttFilter(k)} className={`rounded-full px-2.5 py-1 text-xs font-medium ${attFilter === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{label} · {n}</button>
              ))}
          </div>
          <ul className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-100">
            {people.length === 0 ? <li className="py-6 text-center text-sm text-slate-400">Nobody here</li> : people.map((p) => (
              <li key={p.user_id} className="flex items-center gap-3 px-3 py-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{ backgroundColor: (ATT[p.status] || ATT.off)[2] }}>{initials(p.name)}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-slate-900">{p.name}</div>
                  <div className="truncate text-xs text-slate-500">{p.designation}{p.mode === "WFH" ? " · WFH" : ""}{p.source === "mobile" ? " · app" : ""}</div>
                </div>
                <div className="text-right">
                  <Chip status={p.status} />
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    {p.punch_in ? <>In {time(p.punch_in)}{p.is_late ? <span className="text-amber-600"> · {p.late_minutes}m late</span> : ""}{p.punch_out ? ` · Out ${time(p.punch_out)}` : ""}</> : p.leave_type || ""}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Section>

        {/* ── Tasks ── */}
        <Section testid="dash-tasks" icon={ClipboardList} tint="bg-sky-50 text-sky-600" title="Tasks" subtitle={`${periodLabel} · ${T.open} open right now`} link="/work-hub/tasks" linkLabel="All tasks">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Completed" value={T.completed_in_period} tone="text-emerald-600" sub={<Delta now={T.completed_in_period} prev={T.completed_prev_period} suffix="vs prev." />} />
            <Stat label="Created" value={T.created_in_period} />
            <Stat label="Due today" value={T.due_today} tone={T.due_today ? "text-sky-600" : "text-slate-900"} />
            <Stat label="Overdue" value={T.overdue} tone={T.overdue ? "text-rose-600" : "text-slate-900"} sub={<span className="text-[11px] text-slate-500">{T.on_time_rate}% of open on time</span>} />
          </div>
          <div className="mt-4 h-40">
            <ResponsiveContainer>
              <BarChart data={T.trend} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                <Tooltip />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="created" name="Created" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
                <Bar dataKey="completed" name="Completed" fill="#0ea5e9" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <h3 className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">{T.overdue ? "Most overdue" : "Due today"}</h3>
          {(T.overdue ? T.overdue_list : T.due_today_list).length === 0 ? <Empty>Nothing due — nice.</Empty> : (
            <ul className="divide-y divide-slate-100 rounded-xl border border-slate-100">
              {(T.overdue ? T.overdue_list : T.due_today_list).slice(0, 6).map((t) => (
                <li key={t.task_id} className="flex items-center gap-3 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-900">{t.title}</div>
                    <div className="truncate text-xs text-slate-500">{t.assignees.length ? t.assignees.join(", ") : <span className="text-amber-600">Unassigned</span>} · due {day(t.due_date)}</div>
                  </div>
                  {t.days_overdue > 0 && <span className="shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">{t.days_overdue}d late</span>}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* ── Employees ── */}
        <Section testid="dash-employees" icon={Users} tint="bg-indigo-50 text-indigo-600" title="Employees" subtitle={`${periodLabel} · ranked by work completed`} link="/attendance/staff" linkLabel="Staff profiles">
          <div className="mb-2 flex justify-end">
            <select value={empSort} onChange={(e) => setEmpSort(e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-700">
              <option value="score">Top performers</option><option value="overdue">Most overdue</option><option value="open">Most open tasks</option><option value="present">Days present</option><option value="name">Name A–Z</option>
            </select>
          </div>
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                <tr className="border-b border-slate-100">
                  <th className="px-5 py-2">Employee</th><th className="px-2 py-2">Today</th>
                  <th className="px-2 py-2 text-right" title="Tasks completed in the period">Done</th><th className="px-2 py-2 text-right">Open</th><th className="px-2 py-2 text-right">Late</th>
                  <th className="px-2 py-2 text-right" title="Open leads · follow-ups due">Leads</th><th className="px-5 py-2 text-right" title="Days present in the period">Days</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((e, i) => (
                  <tr key={e.user_id} className="border-b border-slate-50 hover:bg-slate-50/70">
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${i === 0 && empSort === "score" && e.tasks_done > 0 ? "bg-amber-100 text-amber-700" : "bg-indigo-50 text-indigo-700"}`}>{initials(e.name)}</span>
                        <div className="min-w-0"><div className="truncate font-medium text-slate-900">{e.name}</div><div className="truncate text-xs text-slate-500">{e.designation || e.role}</div></div>
                      </div>
                    </td>
                    <td className="px-2 py-2.5">{e.attendance_today ? <Chip status={e.attendance_today} /> : "—"}{e.is_late && <div className="text-[10px] text-amber-600">late</div>}</td>
                    <td className="px-2 py-2.5 text-right font-semibold tabular-nums text-emerald-600">{e.tasks_done}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-slate-700">{e.tasks_open}</td>
                    <td className={`px-2 py-2.5 text-right tabular-nums ${e.tasks_overdue ? "font-semibold text-rose-600" : "text-slate-300"}`}>{e.tasks_overdue || "0"}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-slate-700">{e.leads_open}{e.followups_due > 0 && <span className="text-amber-600"> · {e.followups_due}</span>}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-slate-700">{e.days_present}{e.late_days > 0 && <span className="text-amber-600"> ({e.late_days}L)</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Done = tasks completed in the period · Late = overdue open tasks · Leads = open leads (· follow-ups due) · Days = days present (L = late).</p>
        </Section>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
        <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />Quick links:</span>
        <Link to="/work-hub" className="font-medium text-indigo-600 hover:underline">Work Hub</Link>
        <Link to="/attendance/location-tracking" className="font-medium text-indigo-600 hover:underline">Staff locations</Link>
        <Link to="/reports" className="font-medium text-indigo-600 hover:underline">Reports</Link>
        <span className="inline-flex items-center gap-1"><IndianRupee className="h-3.5 w-3.5" />Won value uses each lead's deal value.</span>
        <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />Won/lost and completed dates use each record's last update.</span>
        <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Times in IST.</span>
      </div>
    </div>
  );
}
